import { request } from "@/Api";
import ProfileAvatar from "@/common/ProfileAvatar";
import { CustomBox, FlexCell, FlexRow } from "@/common/StyledComponents";
import { Heading } from "@/common/typography";
import { ProfileListItem } from "@/profiles_common/CommonComponents";
import {
    Button,
    Chip,
    CircularProgress,
    Grid,
    Modal,
    TableCell,
    TextField,
    useTheme,
} from "@mui/material";
import { useState } from "react";
import { User, Buildings } from "@solar-icons/react/ssr";
import {
    VENDOR_STATUS_INVITE_PENDING,
    getVendorStatusColor,
} from "@/vendor/constants";
import { useParams } from "react-router-dom";
import { VendorStatusChip } from "./PurchaseOrder";
import ApiAutocomplete from "@/common/ApiAutocomplete";

export default function PurchaseOrderPopup({ isOpen, handleClose, onAdd }) {
    const theme = useTheme();
    const { projectId } = useParams();
    const [selectedVendors, setSelectedVendors] = useState([]);
    const [waiting, setWaiting] = useState(false);

    const handleVendorsChange = (event, newValue) => {
        const lastChosenOption = newValue[newValue.length - 1];
        if (typeof lastChosenOption == "object") {
            setSelectedVendors(newValue);
        } else if (typeof lastChosenOption == "string") {
            setSelectedVendors([
                ...newValue.slice(0, newValue.length - 1),
                { email: lastChosenOption },
            ]);
        } else {
            setSelectedVendors(newValue);
        }
    };
    const handleInviteVendors = () => {
        setWaiting(true);
        const purchaseOrdersAddData = {
            existing_vendors: selectedVendors
                .filter((vendor) => vendor.id)
                .map((vendor) => ({
                    vendor_id: vendor.id,
                    amount: vendor.po_amount,
                    project_id: projectId,
                })),
            new_vendors: selectedVendors
                .filter((vendor) => !vendor.id)
                .map((vendor) => ({
                    vendor_email: vendor.email,
                    amount: vendor.po_amount,
                    project_id: projectId,
                })),
        };
        request
            .post(`purchase-orders`, purchaseOrdersAddData)
            .then((response) => {
                const newPurchaseOrders = response.data;
                newPurchaseOrders.forEach((po) => {
                    onAdd && onAdd(po);
                });
                setSelectedVendors([]);
                handleClose();
            })
            .finally(() => {
                setWaiting(false);
            });
    };
    const handleVendorPurchaseOrderChange = (vendor, field, value) => {
        const updatedVendors = selectedVendors.map((v) =>
            vendor.email == v.email ? { ...v, [field]: value } : v,
        );
        setSelectedVendors(updatedVendors);
    };
    return (
        <Modal open={isOpen} onClose={handleClose}>
            <CustomBox
                sx={{
                    maxHeight: "90vh",
                    padding: 2,
                    paddingLeft: 4,
                    paddingRight: 4,
                    background: theme.palette.background.default,
                    width: "60%",
                    minWidth: 800,
                }}
            >
                <h3 style={{ marginBottom: 0 }}>Add new vendors</h3>
                <Grid
                    sx={{ fontSize: 12 }}
                    container
                    width={"100%"}
                    spacing={1}
                    justifyContent={"right"}
                    paddingBottom={2}
                >
                    <Grid item>
                        <Legend
                            label={"New Vendor"}
                            color={theme.palette.info.main}
                        />
                    </Grid>
                    <Grid item>
                        <Legend
                            label={"Onboarded"}
                            color={theme.palette.success.main}
                        />
                    </Grid>
                </Grid>
                <ApiAutocomplete
                    fullOptionData
                    onInputResponseLimit={10}
                    onChange={handleVendorsChange}
                    value={selectedVendors}
                    freeSolo
                    multiple
                    apiRoute={"vendors/search"}
                    fields={["name", "email"]}
                    renderTags={(value, getTagProps) =>
                        value.map((vendor, index) => {
                            const vendorIsPending =
                                !vendor.id ||
                                vendor.status == VENDOR_STATUS_INVITE_PENDING;
                            const chipColor = vendorIsPending
                                ? "info"
                                : "success";
                            return (
                                <Chip
                                    key={vendor.email}
                                    label={vendor.email}
                                    {...getTagProps({ index })}
                                    color={chipColor}
                                />
                            );
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            fullWidth
                            {...params}
                            label=""
                            InputLabelProps={{ shrink: false }}
                        />
                    )}
                    renderOption={(props, vendor, { selected }) => (
                        <div {...props} key={vendor.id}>
                            <VendorOption vendor={vendor} />
                        </div>
                    )}
                />
                <div hidden={!selectedVendors.length}>
                    <h4>Purchase Orders</h4>
                    <VendorPoRowHeader />
                    {selectedVendors.map((vendor, index) => (
                        <VendorPoRow
                            key={index}
                            vendor={vendor}
                            onInput={handleVendorPurchaseOrderChange}
                        />
                    ))}
                </div>
                <Grid
                    container
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    paddingTop={4}
                    paddingBottom={2}
                >
                    <Grid item>
                        An invitation to join the selected vendor will be sent
                        to the above email addresses.
                    </Grid>
                    <Grid item>
                        <Grid container spacing={2}>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </Button>
                            </Grid>
                            <Grid item>
                                {waiting ? (
                                    <CircularProgress />
                                ) : (
                                    <Button
                                        variant="contained"
                                        onClick={handleInviteVendors}
                                    >
                                        Invite Vendors
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </CustomBox>
        </Modal>
    );
}

const VendorOption = ({ vendor }) => {
    const theme = useTheme();
    const muiColor = getVendorStatusColor(vendor.status);
    const paletteColor = theme.palette[muiColor] || theme.palette.info;
    return (
        <>
            <Grid container alignItems={"center"} spacing={2}>
                <Grid item>
                    <ProfileAvatar
                        name={vendor.name}
                        primary={paletteColor.main}
                        secondary={paletteColor.light}
                    />
                </Grid>
                <Grid item>
                    <span>{vendor.email}</span>
                    <ProfileListItem
                        icon={
                            vendor.account_type == "Freelance" ? (
                                <User />
                            ) : (
                                <Buildings />
                            )
                        }
                        text={vendor.name}
                        secondary={paletteColor.light}
                    />
                </Grid>
            </Grid>
        </>
    );
};

const VendorPoRowHeader = () => {
    return (
        <FlexRow style={{ paddingBottom: 20 }}>
            <FlexCell align="left">Email</FlexCell>
            <FlexCell align="left">Vendor Status</FlexCell>
            <FlexCell align="right">PO Amount</FlexCell>
        </FlexRow>
    );
};

const VendorPoRow = ({ vendor, onInput }) => {
    const handleKeyDown = (e) => {
        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
            e.preventDefault();
        }
    };
    const handleVendorPurchaseOrderChange = (field) => (event) => {
        if (event.key === "ArrowUp" || event.key === "ArrowDown") {
            event.preventDefault();
        }
        const value = event.target.value;
        onInput(vendor, field, value);
    };
    return (
        <FlexRow style={{ paddingBottom: 10 }}>
            <FlexCell align="left">
                <span>{vendor.email}</span>
            </FlexCell>
            <FlexCell align="left">
                <VendorStatusChip vendor={vendor} />
            </FlexCell>
            <FlexCell align="right">
                <TextField
                    type="number"
                    variant="outlined"
                    size="small"
                    fullWidth
                    inputProps={{ style: { textAlign: "right" } }}
                    InputProps={{
                        startAdornment: (
                            <span style={{ color: "gray" }}>
                                {vendor.payment_currency}
                            </span>
                        ),
                    }}
                    onChange={handleVendorPurchaseOrderChange("po_amount")}
                    onKeyDown={handleKeyDown}
                />
            </FlexCell>
        </FlexRow>
    );
};

const Legend = ({ label, color }) => {
    return (
        <Grid container alignItems={"center"} spacing={1}>
            <Grid item>
                <div
                    style={{
                        width: 15,
                        height: 15,
                        backgroundColor: color,
                        borderRadius: "50%",
                    }}
                />
            </Grid>
            <Grid item>
                <span style={{ color: "gray" }}>{label}</span>
            </Grid>
        </Grid>
    );
};
